import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import ImageUploader from '../../components/admin/ImageUploader';
import { resolveMedia } from '../../context/SettingsContext';

const emptyProduct = {
  name: '',
  description: '',
  shortDescription: '',
  category: '',
  productType: 'simple',
  price: '',
  compareAtPrice: '',
  stock: '',
  images: [],
  variantAttributes: [],
  variants: [],
  groupedProducts: [],
  isActive: true,
  isFeatured: false,
};

// Cartesian product of variant attribute option lists
function generateCombinations(attributes) {
  if (!attributes || !attributes.length) return [];
  return attributes.reduce(
    (acc, attr) => {
      const next = [];
      acc.forEach((combo) => {
        (attr.options || []).forEach((opt) => {
          next.push({ ...combo, [attr.name]: opt });
        });
      });
      return next;
    },
    [{}]
  );
}

function formatAttrString(attributes) {
  if (!attributes) return '';
  const entries = attributes instanceof Map ? Array.from(attributes.entries()) : Object.entries(attributes);
  return entries.map(([k, val]) => `${k}: ${val}`).join(' | ');
}

function isAttrMatch(vAttrs, comboAttrs) {
  if (!vAttrs || !comboAttrs) return false;
  return Object.entries(comboAttrs).every(([k, val]) => {
    const vVal = vAttrs instanceof Map ? vAttrs.get(k) : vAttrs[k];
    return vVal === val;
  });
}

export default function AdminProductForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyProduct);
  const [categories, setCategories] = useState([]);
  const [globalVariants, setGlobalVariants] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [attrName, setAttrName] = useState('');
  const [attrOptions, setAttrOptions] = useState('');
  const [saving, setSaving] = useState(false);

  // Quick Select & Search states for Predefined Global Variant Attributes Dropdown
  const [variantSearch, setVariantSearch] = useState('');
  const [variantDropdownOpen, setVariantDropdownOpen] = useState(false);
  const variantDropdownRef = useRef(null);

  // State for inline adding options inside an active attached attribute
  const [addingOptionForAttr, setAddingOptionForAttr] = useState(null);
  const [newOptionValue, setNewOptionValue] = useState('');

  useEffect(() => {
    api.get('/categories').then((res) => setCategories(res.data));
    api.get('/variants').then((res) => setGlobalVariants(res.data)).catch(() => {});
    api.get('/products/admin/all').then((res) => setAllProducts(res.data));
    if (isEdit) {
      api.get(`/products/admin/all`).then((res) => {
        const p = res.data.find((x) => x._id === id);
        if (p) {
          setForm({
            ...p,
            category: p.category?._id || p.category || '',
            variants: (p.variants || []).map((v) => ({
              ...v,
              attributes: v.attributes ? { ...v.attributes } : {},
              compareAtPrice: v.compareAtPrice !== undefined && v.compareAtPrice !== null ? v.compareAtPrice : '',
              price: v.price !== undefined && v.price !== null ? v.price : '',
              stock: v.stock !== undefined && v.stock !== null ? v.stock : '',
              sku: v.sku || '',
            })),
            variantAttributes: p.variantAttributes || [],
            groupedProducts: p.groupedProducts || [],
          });
        }
      });
    }
  }, [id, isEdit]);

  // Click outside to close variant dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (variantDropdownRef.current && !variantDropdownRef.current.contains(e.target)) {
        setVariantDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper to re-generate variant combinations whenever attributes change (Leaves price, stock & sku blank for manual entry)
  const applyAttributesAndGenerate = (nextAttributes) => {
    const combos = generateCombinations(nextAttributes);
    setForm((f) => ({
      ...f,
      variantAttributes: nextAttributes,
      variants: combos.map((attributes) => {
        const existing = (f.variants || []).find((v) => isAttrMatch(v.attributes, attributes));
        return existing || { attributes, price: '', compareAtPrice: '', stock: '', sku: '' };
      }),
    }));
  };

  const addAttribute = () => {
    if (!attrName.trim() || !attrOptions.trim()) return;
    const options = attrOptions.split(',').map((o) => o.trim()).filter(Boolean);
    const nextAttrs = [...form.variantAttributes, { name: attrName.trim(), options }];
    applyAttributesAndGenerate(nextAttrs);
    setAttrName('');
    setAttrOptions('');
    toast.success(`Added attribute "${attrName.trim()}" and updated combinations`);
  };

  const handleAddOptionToAttribute = (targetAttrName, rawOption) => {
    const trimmed = rawOption?.trim();
    if (!trimmed) return;
    const attr = form.variantAttributes.find((a) => a.name.toLowerCase() === targetAttrName.toLowerCase());
    if (!attr) return;
    if (attr.options.some((o) => o.toLowerCase() === trimmed.toLowerCase())) {
      toast.error(`Option "${trimmed}" already exists in "${targetAttrName}"`);
      return;
    }
    const nextAttrs = form.variantAttributes.map((a) => {
      if (a.name.toLowerCase() === targetAttrName.toLowerCase()) {
        return { ...a, options: [...a.options, trimmed] };
      }
      return a;
    });
    applyAttributesAndGenerate(nextAttrs);
    setNewOptionValue('');
    setAddingOptionForAttr(null);
    toast.success(`Added option "${trimmed}" to "${targetAttrName}"`);
  };

  const handleRemoveOptionFromAttribute = (targetAttrName, optionValue) => {
    const attr = form.variantAttributes.find((a) => a.name.toLowerCase() === targetAttrName.toLowerCase());
    if (!attr) return;
    if (attr.options.length <= 1) {
      toast.error(`Attribute "${targetAttrName}" must have at least 1 option, or remove the attribute.`);
      return;
    }
    const nextAttrs = form.variantAttributes.map((a) => {
      if (a.name.toLowerCase() === targetAttrName.toLowerCase()) {
        return { ...a, options: a.options.filter((o) => o !== optionValue) };
      }
      return a;
    });
    applyAttributesAndGenerate(nextAttrs);
    toast.success(`Removed option "${optionValue}" from "${targetAttrName}"`);
  };

  const addPredefinedAttribute = (globalAttr) => {
    const exists = form.variantAttributes.find((a) => a.name.toLowerCase() === globalAttr.name.toLowerCase());
    if (exists) {
      toast.error(`Attribute "${globalAttr.name}" is already added`);
      return;
    }
    const nextAttrs = [...form.variantAttributes, { name: globalAttr.name, options: [...globalAttr.options] }];
    applyAttributesAndGenerate(nextAttrs);
    toast.success(`Added "${globalAttr.name}" and generated all variant combinations`);
  };

  const addAllPredefinedAttributes = () => {
    const unadded = globalVariants.filter(
      (gv) => !form.variantAttributes.some((a) => a.name.toLowerCase() === gv.name.toLowerCase())
    );
    if (unadded.length === 0) {
      toast.error('All global attributes are already added');
      return;
    }
    const nextAttrs = [
      ...form.variantAttributes,
      ...unadded.map((gv) => ({ name: gv.name, options: [...gv.options] })),
    ];
    applyAttributesAndGenerate(nextAttrs);
    toast.success(`Added all ${unadded.length} global attributes & updated variant matrix`);
  };

  const clearAllAttributes = () => {
    if (!form.variantAttributes.length) return;
    if (!window.confirm('Clear all variant attributes and combinations?')) return;
    applyAttributesAndGenerate([]);
    toast.success('Cleared all variant attributes');
  };

  const removeAttribute = (name) => {
    const nextAttrs = form.variantAttributes.filter((a) => a.name !== name);
    applyAttributesAndGenerate(nextAttrs);
    toast.success(`Removed attribute "${name}"`);
  };

  const generateVariantsManually = () => {
    if (!form.variantAttributes.length) {
      toast.error('Please add at least one attribute first');
      return;
    }
    applyAttributesAndGenerate(form.variantAttributes);
    toast.success('Regenerated all variant combinations');
  };

  const updateVariant = (index, field, value) => {
    setForm((f) => {
      const variants = [...f.variants];
      variants[index] = { ...variants[index], [field]: value };
      return { ...f, variants };
    });
  };

  const addImage = (url) => {
    setForm((f) => ({ ...f, images: [...f.images, url] }));
  };

  const removeImage = (url) => {
    setForm((f) => ({ ...f, images: f.images.filter((i) => i !== url) }));
  };

  const toggleGroupedProduct = (productId) => {
    setForm((f) => {
      const exists = f.groupedProducts.find((g) => (g.product?._id || g.product) === productId);
      if (exists) {
        return { ...f, groupedProducts: f.groupedProducts.filter((g) => (g.product?._id || g.product) !== productId) };
      }
      return { ...f, groupedProducts: [...f.groupedProducts, { product: productId, quantity: 1 }] };
    });
  };

  const updateGroupedQty = (productId, qty) => {
    setForm((f) => ({
      ...f,
      groupedProducts: f.groupedProducts.map((g) =>
        (g.product?._id || g.product) === productId ? { ...g, quantity: Number(qty) } : g
      ),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let mainPrice = Number(form.price) || 0;
      let mainStock = Number(form.stock) || 0;
      let mainCompareAtPrice = form.compareAtPrice ? Number(form.compareAtPrice) : undefined;

      if (form.productType === 'variable' && form.variants.length > 0) {
        const validPrices = form.variants.map((v) => Number(v.price) || 0).filter((p) => p > 0);
        if (validPrices.length) mainPrice = Math.min(...validPrices);

        const validComparePrices = form.variants.map((v) => Number(v.compareAtPrice) || 0).filter((p) => p > 0);
        if (validComparePrices.length) mainCompareAtPrice = Math.min(...validComparePrices);

        mainStock = form.variants.reduce((acc, v) => acc + (Number(v.stock) || 0), 0);
      }

      const payload = {
        ...form,
        price: mainPrice,
        compareAtPrice: mainCompareAtPrice,
        stock: mainStock,
        variants: form.variants.map((v) => ({
          ...v,
          price: v.price === '' || v.price === null || v.price === undefined ? 0 : Number(v.price),
          compareAtPrice:
            v.compareAtPrice === '' || v.compareAtPrice === null || v.compareAtPrice === undefined
              ? undefined
              : Number(v.compareAtPrice),
          stock: v.stock === '' || v.stock === null || v.stock === undefined ? 0 : Number(v.stock),
          sku: v.sku || '',
        })),
        groupedProducts: form.groupedProducts.map((g) => ({
          product: g.product?._id || g.product,
          quantity: g.quantity,
        })),
        isActive: true,
      };

      if (isEdit) {
        await api.put(`/products/${id}`, payload);
        toast.success('Product updated successfully');
      } else {
        await api.post('/products', payload);
        toast.success('Product created successfully');
      }
      navigate('/admin/products');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save product');
    } finally {
      setSaving(false);
    }
  };

  // Filtered Global Variant Attributes for Dropdown Search
  const filteredGlobalVariants = globalVariants.filter((gv) => {
    const query = variantSearch.toLowerCase().trim();
    if (!query) return true;
    const nameMatch = gv.name.toLowerCase().includes(query);
    const optionsMatch = (gv.options || []).some((opt) => opt.toLowerCase().includes(query));
    return nameMatch || optionsMatch;
  });

  return (
    <div className="space-y-6 text-[#2B1810] max-w-4xl">
      <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#2B1810]">{isEdit ? 'Edit Product Details' : 'Add New Product'}</h1>
          <p className="text-xs text-[#8A796C]">Configure single products, variable items with option attributes, or combo bundle groups.</p>
        </div>
        <Link
          to="/admin/products"
          className="px-4 py-2 bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#D9D0C8] text-[#2B1810] text-xs font-bold uppercase rounded-lg transition-colors inline-flex items-center gap-1.5"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          <span>Back to Products</span>
        </Link>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-4">
          <div>
            <label className="label">Product Name</label>
            <input required className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Pure Sandalwood Mala" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Category / Subcategory</label>
              <select required className="input-field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="">Select Category / Subcategory</option>
                {categories
                  .filter((c) => !c.parent)
                  .map((parent) => {
                    const subs = categories.filter((c) => (c.parent?._id || c.parent) === parent._id);
                    return (
                      <React.Fragment key={parent._id}>
                        <option value={parent._id} className="font-bold text-[#2B1810]">
                          {parent.name} (Main Category)
                        </option>
                        {subs.map((sub) => (
                          <option key={sub._id} value={sub._id} className="text-[#665448]">
                            &nbsp;&nbsp;&nbsp;↳ {sub.name} (Subcategory)
                          </option>
                        ))}
                      </React.Fragment>
                    );
                  })}
              </select>
            </div>

            <div>
              <label className="label">Product Type</label>
              <select className="input-field" value={form.productType} onChange={(e) => setForm({ ...form, productType: e.target.value })}>
                <option value="simple">Simple (Single Item)</option>
                <option value="variable">Variable (Multiple Variants / Sizes / Colours)</option>
                <option value="grouped">Grouped (Combo / Bundle Pack)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="label">Short Description</label>
            <input className="input-field" value={form.shortDescription} onChange={(e) => setForm({ ...form, shortDescription: e.target.value })} placeholder="Brief 1-line summary" />
          </div>

          <div>
            <label className="label">Full Detailed Description</label>
            <textarea rows={4} className="input-field" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Full description of craftsmanship, materials, dimensions..." />
          </div>

          <div className="flex gap-6 pt-2">
            <label className="flex items-center gap-2 text-xs font-bold text-[#2B1810]">
              <input type="checkbox" className="accent-[#C49A45] w-4 h-4" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              Active (Visible on Storefront)
            </label>
            <label className="flex items-center gap-2 text-xs font-bold text-[#2B1810]">
              <input type="checkbox" className="accent-[#C49A45] w-4 h-4" checked={form.isFeatured} onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })} />
              Featured (Show on Homepage)
            </label>
          </div>
        </div>

        {/* Product Images Uploader */}
        <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-4">
          <h3 className="font-heading text-base font-bold text-[#2B1810] border-b border-[#E8DFD8] pb-3">Product Images</h3>
          <div className="flex flex-wrap gap-3 mb-2">
            {form.images.map((img) => (
              <div key={img} className="relative group">
                <img src={resolveMedia(img)} alt="" className="w-20 h-20 object-cover rounded-lg border border-[#E8DFD8]" />
                <button type="button" onClick={() => removeImage(img)} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center font-bold shadow">×</button>
              </div>
            ))}
          </div>
          <ImageUploader endpoint="/uploads/image" onUploaded={addImage} label="Add Product Photo" hint="Recommended: 1200 × 1200 px (square). Use the same framing for every gallery image. JPG, PNG or WEBP; maximum 5 MB." />
        </div>

        {/* Simple Product Fields */}
        {form.productType === 'simple' && (
          <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm grid md:grid-cols-3 gap-4">
            <div>
              <label className="label">Price (₹)</label>
              <input required type="number" className="input-field" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <label className="label">Compare-at Price (₹)</label>
              <input type="number" className="input-field" value={form.compareAtPrice} onChange={(e) => setForm({ ...form, compareAtPrice: e.target.value })} />
            </div>
            <div>
              <label className="label">Stock Inventory</label>
              <input required type="number" className="input-field" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
            </div>
          </div>
        )}

        {/* Variable Product Fields */}
        {form.productType === 'variable' && (
          <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-5">
            <div className="border-b border-[#E8DFD8] pb-3 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-heading text-base font-bold text-[#2B1810]">Variant Attributes & Combinations</h3>
                <p className="text-xs text-[#8A796C]">Add attributes like Size, Material, Weight or select from predefined global attributes.</p>
              </div>
              <Link
                to="/admin/variants"
                target="_blank"
                className="text-[11px] font-bold text-[#C49A45] hover:underline inline-flex items-center gap-1"
              >
                <span>Manage Global Attributes</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </Link>
            </div>
            
            {/* Quick Select Predefined Global Attributes Section with Pure Searchable Dropdown (No Direct Buttons) */}
            {globalVariants.length > 0 && (
              <div className="bg-[#FAF8F5] border border-[#E8DFD8] p-4 rounded-xl space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-md bg-[#C49A45]/15 text-[#8C6B28] flex items-center justify-center font-bold text-xs">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
                    </span>
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-[#2B1810] block">
                        Quick Select Predefined Global Variant Attributes
                      </span>
                      <span className="text-[10px] text-[#8A796C]">
                        {globalVariants.length} Predefined Attributes Available in Dropdown · {form.variantAttributes.length} Attached
                      </span>
                    </div>
                  </div>

                  {/* Header Action Buttons: Add All / Clear All */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={addAllPredefinedAttributes}
                      className="px-3 py-1.5 bg-[#C49A45] hover:bg-[#B08736] text-white text-[10px] font-bold uppercase tracking-wider rounded-lg shadow-xs transition-colors inline-flex items-center gap-1.5"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      <span>Add All Attributes</span>
                    </button>

                    {form.variantAttributes.length > 0 && (
                      <button
                        type="button"
                        onClick={clearAllAttributes}
                        className="px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-colors"
                        title="Clear all attached attributes"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                </div>

                {/* Pure Searchable Dropdown Selector (No direct button pills) */}
                <div className="relative" ref={variantDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setVariantDropdownOpen(!variantDropdownOpen)}
                    className="w-full bg-white border border-[#D9D0C8] rounded-xl px-4 py-2.5 text-xs text-left text-[#2B1810] font-semibold flex items-center justify-between hover:border-[#C49A45] transition-all shadow-2xs"
                  >
                    <div className="flex items-center gap-2 truncate">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C49A45" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                      <span className="truncate">
                        {form.variantAttributes.length > 0
                          ? `Select or Search Predefined Global Attributes (${form.variantAttributes.length} currently attached)...`
                          : 'Select Predefined Global Attributes from Dropdown...'}
                      </span>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`text-[#8A796C] transition-transform duration-200 shrink-0 ${variantDropdownOpen ? 'rotate-180' : ''}`}><path d="M6 9l6 6 6-6"/></svg>
                  </button>

                  {/* Dropdown Popup with Embedded Search & List */}
                  {variantDropdownOpen && (
                    <div className="absolute left-0 right-0 mt-1.5 bg-white border border-[#E8DFD8] rounded-2xl shadow-2xl z-40 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                      {/* Search Bar inside Dropdown */}
                      <div className="p-3 border-b border-[#E8DFD8] bg-[#FAF8F5] flex items-center gap-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C49A45" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <input
                          type="text"
                          value={variantSearch}
                          onChange={(e) => setVariantSearch(e.target.value)}
                          placeholder="Type to filter global attributes (e.g. Size, Colour, Wood Type)..."
                          className="w-full bg-transparent text-xs text-[#2B1810] placeholder-[#8A796C] outline-none font-medium"
                          autoFocus
                        />
                        {variantSearch && (
                          <button
                            type="button"
                            onClick={() => setVariantSearch('')}
                            className="text-[#8A796C] hover:text-[#2B1810] text-xs px-1"
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      {/* Header in Dropdown */}
                      <div className="px-4 py-2 text-[9px] font-bold uppercase tracking-wider text-[#8A796C] bg-white border-b border-[#E8DFD8] flex items-center justify-between">
                        <span>Available Global Attributes ({filteredGlobalVariants.length})</span>
                        <button
                          type="button"
                          onClick={addAllPredefinedAttributes}
                          className="text-[#C49A45] hover:underline font-bold"
                        >
                          + Add All
                        </button>
                      </div>

                      {/* Scrollable List of Global Attributes */}
                      <div className="max-h-60 overflow-y-auto divide-y divide-[#E8DFD8] thin-scrollbar">
                        {filteredGlobalVariants.length === 0 ? (
                          <div className="p-6 text-center text-xs text-[#8A796C]">
                            No global attributes match "{variantSearch}".
                          </div>
                        ) : (
                          filteredGlobalVariants.map((gv) => {
                            const isAdded = form.variantAttributes.some(
                              (a) => a.name.toLowerCase() === gv.name.toLowerCase()
                            );
                            return (
                              <div
                                key={gv._id}
                                onClick={() => {
                                  if (isAdded) {
                                    removeAttribute(gv.name);
                                  } else {
                                    addPredefinedAttribute(gv);
                                  }
                                }}
                                className={`p-3 text-xs flex items-start justify-between gap-3 cursor-pointer transition-colors ${
                                  isAdded ? 'bg-amber-50/60 font-bold text-[#2B1810]' : 'hover:bg-[#FAF8F5] text-[#4A3B32]'
                                }`}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-bold text-xs text-[#2B1810]">{gv.name}</span>
                                    {isAdded && (
                                      <span className="text-[9px] font-bold px-1.5 py-0.2 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                                        ✓ Attached
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[10px] text-[#8A796C] mt-0.5 truncate">
                                    Options: {gv.options.join(', ')}
                                  </p>
                                </div>
                                <span className={`text-xs font-bold shrink-0 px-2 py-0.5 rounded ${
                                  isAdded
                                    ? 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                                    : 'bg-[#C49A45]/15 text-[#8C6B28] hover:bg-[#C49A45]/25'
                                }`}>
                                  {isAdded ? 'Remove' : '+ Add'}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Custom Attribute Section */}
            <div className="space-y-2 pt-1">
              <label className="text-xs font-bold uppercase tracking-wider text-[#2B1810] block">
                Or Enter Custom Attribute:
              </label>
              <div className="flex gap-3">
                <input
                  placeholder="Attribute Name (e.g. Size, Weight, Color)"
                  className="input-field"
                  value={attrName}
                  onChange={(e) => setAttrName(e.target.value)}
                />
                <input
                  placeholder="Options (comma separated e.g. 6mm, 8mm, 10mm)"
                  className="input-field"
                  value={attrOptions}
                  onChange={(e) => setAttrOptions(e.target.value)}
                />
                <button
                  type="button"
                  onClick={addAttribute}
                  className="bg-[#2B1810] hover:bg-[#1C100A] text-white font-bold text-xs uppercase tracking-wider px-5 rounded-lg shrink-0 transition-colors"
                >
                  Add Custom
                </button>
              </div>
            </div>

            {/* Active Variant Attributes Pills with Inline + Option */}
            <div className="space-y-2.5 pt-1">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#8A796C] block">
                  Active Attached Attributes ({form.variantAttributes.length}):
                </span>
                {form.variantAttributes.length > 0 && (
                  <span className="text-[10px] text-[#8A796C]">
                    Click <strong className="text-[#C49A45]">+ Option</strong> inside any attribute to add more options
                  </span>
                )}
              </div>

              {form.variantAttributes.length === 0 ? (
                <p className="text-xs text-[#8A796C] italic bg-[#FAF8F5] p-3 rounded-lg border border-dashed border-[#D9D0C8]">
                  No variant attributes attached yet. Select from the dropdown above or enter a custom attribute.
                </p>
              ) : (
                <div className="space-y-2">
                  {form.variantAttributes.map((a) => (
                    <div
                      key={a.name}
                      className="bg-white border border-[#C49A45]/80 rounded-xl p-3 shadow-xs flex flex-wrap items-center justify-between gap-3"
                    >
                      <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
                        <span className="font-bold text-xs text-[#2B1810] flex items-center gap-1.5 shrink-0 bg-[#FAF8F5] px-2.5 py-1 rounded-lg border border-[#E8DFD8]">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#C49A45]" />
                          {a.name}:
                        </span>

                        {/* Individual Option Pills with remove x */}
                        <div className="flex flex-wrap items-center gap-1.5">
                          {a.options.map((opt) => (
                            <span
                              key={opt}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#FAF8F5] border border-[#D9D0C8] text-xs font-semibold text-[#2B1810]"
                            >
                              <span>{opt}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveOptionFromAttribute(a.name, opt)}
                                className="text-[#8A796C] hover:text-red-600 font-bold text-xs ml-1 transition-colors"
                                title={`Remove option "${opt}"`}
                              >
                                ×
                              </button>
                            </span>
                          ))}

                          {/* Inline + Add Option Input / Trigger */}
                          {addingOptionForAttr === a.name ? (
                            <div className="inline-flex items-center gap-1.5 bg-[#FAF8F5] p-1 rounded-lg border border-[#C49A45]">
                              <input
                                type="text"
                                autoFocus
                                value={newOptionValue}
                                onChange={(e) => setNewOptionValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddOptionToAttribute(a.name, newOptionValue);
                                  } else if (e.key === 'Escape') {
                                    setAddingOptionForAttr(null);
                                    setNewOptionValue('');
                                  }
                                }}
                                placeholder="e.g. XL"
                                className="w-24 px-2 py-0.5 text-xs bg-white border border-[#D9D0C8] rounded outline-none text-[#2B1810] font-medium"
                              />
                              <button
                                type="button"
                                onClick={() => handleAddOptionToAttribute(a.name, newOptionValue)}
                                className="px-2.5 py-0.5 bg-[#C49A45] text-white text-xs font-bold rounded hover:bg-[#B08736] transition-colors"
                              >
                                Add
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setAddingOptionForAttr(null);
                                  setNewOptionValue('');
                                }}
                                className="text-[#8A796C] hover:text-[#2B1810] text-xs px-1"
                                title="Cancel"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setAddingOptionForAttr(a.name);
                                setNewOptionValue('');
                              }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-[#8C6B28] border border-[#C49A45]/40 rounded-lg text-xs font-bold transition-all shadow-2xs"
                              title={`Add new option to "${a.name}"`}
                            >
                              <span className="text-sm font-bold leading-none">+</span>
                              <span>Option</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Remove entire attribute button */}
                      <button
                        type="button"
                        onClick={() => removeAttribute(a.name)}
                        className="text-red-600 hover:text-red-800 p-1.5 hover:bg-rose-50 rounded-lg transition-colors shrink-0"
                        title={`Remove entire "${a.name}" attribute`}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {form.variantAttributes.length > 0 && (
              <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
                <button
                  type="button"
                  onClick={generateVariantsManually}
                  className="bg-[#2B1810] hover:bg-[#1C100A] text-white font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-lg shadow transition-colors inline-flex items-center gap-1.5"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/></svg>
                  <span>Re-Generate Combinations ({form.variants.length} Matrix Rows)</span>
                </button>
                <span className="text-xs text-[#8A796C]">
                  Enter specific market price, selling price, stock inventory, and SKU for each combination below.
                </span>
              </div>
            )}

            {/* Generated Variant Matrix Table */}
            {form.variants.length > 0 && (
              <div className="overflow-x-auto border border-[#E8DFD8] rounded-xl mt-4">
                <table className="w-full text-xs text-left">
                  <thead className="bg-[#FAF8F5] border-b border-[#E8DFD8] font-heading uppercase font-bold text-[#2B1810]">
                    <tr>
                      <th className="p-3 min-w-[150px]">Variant Combination</th>
                      <th className="p-3 min-w-[140px]">Market Price (₹)</th>
                      <th className="p-3 min-w-[140px]">Sell Price (₹)</th>
                      <th className="p-3 min-w-[110px]">Stock</th>
                      <th className="p-3 min-w-[120px]">SKU</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8DFD8]">
                    {form.variants.map((v, i) => (
                      <tr key={i} className="hover:bg-[#FAF8F5] transition-colors">
                        <td className="p-3 font-bold text-[#2B1810]">
                          {formatAttrString(v.attributes)}
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            placeholder="Enter Market Price (₹)"
                            className="input-field py-1"
                            value={v.compareAtPrice === undefined || v.compareAtPrice === null ? '' : v.compareAtPrice}
                            onChange={(e) => updateVariant(i, 'compareAtPrice', e.target.value)}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            placeholder="Enter Sell Price (₹)"
                            className="input-field py-1"
                            value={v.price === undefined || v.price === null ? '' : v.price}
                            onChange={(e) => updateVariant(i, 'price', e.target.value)}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            placeholder="Enter Stock"
                            className="input-field py-1"
                            value={v.stock === undefined || v.stock === null ? '' : v.stock}
                            onChange={(e) => updateVariant(i, 'stock', e.target.value)}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="text"
                            placeholder="e.g. SKU-01"
                            className="input-field py-1"
                            value={v.sku || ''}
                            onChange={(e) => updateVariant(i, 'sku', e.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Grouped Product Fields */}
        {form.productType === 'grouped' && (
          <div className="bg-[#FAF8F5] border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-4">
            <h3 className="font-heading text-base font-bold text-[#2B1810] border-b border-[#E8DFD8] pb-3">Combo Bundle Contents</h3>
            <p className="text-xs text-[#8A796C]">Select sub-products included in this combo pack and set the bundle quantity.</p>

            <div className="space-y-2 max-h-72 overflow-y-auto divide-y divide-[#E8DFD8] thin-scrollbar">
              {allProducts.filter((p) => p._id !== id).map((p) => {
                const selected = form.groupedProducts.find((g) => (g.product?._id || g.product) === p._id);
                return (
                  <div key={p._id} className="flex items-center gap-3 text-xs py-2">
                    <input type="checkbox" className="accent-[#C49A45] w-4 h-4" checked={!!selected} onChange={() => toggleGroupedProduct(p._id)} />
                    <span className="flex-1 font-bold text-[#2B1810]">{p.name}</span>
                    {selected && (
                      <input
                        type="number"
                        min={1}
                        className="input-field w-20 py-1"
                        value={selected.quantity}
                        onChange={(e) => updateGroupedQty(p._id, e.target.value)}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="label">Combo Price (₹)</label>
                <input type="number" className="input-field" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <label className="label">Combo Stock</label>
                <input type="number" className="input-field" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
              </div>
            </div>
          </div>
        )}

        <button disabled={saving} className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-widest px-8 py-3.5 rounded-lg shadow-lg transition-colors inline-flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
          {saving ? 'SAVING PRODUCT...' : isEdit ? 'UPDATE PRODUCT' : 'CREATE PRODUCT'}
        </button>
      </form>
    </div>
  );
}
